import {
  isComponentHidden,
  sortComponentsByGrid,
  getComponentDisplayName,
  getViewportAnimationSource,
} from './defaults';

export const ANIMATION_TIMING_MODES = [
  {
    id: 'solo',
    label: 'Indépendante',
    description: 'Le composant s’anime selon son propre déclencheur.',
  },
  {
    id: 'section',
    label: 'Avec la section',
    description: 'Démarre quand la section s’anime, avec un décalage dans la séquence.',
  },
  {
    id: 'after',
    label: 'Après un composant',
    description: 'Apparaît après un autre composant de la même section.',
  },
];

export const DEFAULT_COMPONENT_ANIMATION = {
  enabled: false,
  preset: 'fadeInUp',
  trigger: 'scroll',
  duration: 700,
  delay: 0,
  easing: 'ease-out',
  distance: 48,
  scale: 0.88,
  rotate: 8,
  blur: 6,
  scrollThreshold: 0.15,
  scrollOnce: true,
  scrollOffset: 0,
  parallaxSpeed: 0.35,
  parallaxDirection: 'up',
  timing: 'solo',
  afterComponentId: '',
  delayAfterMs: 0,
  sectionOrder: 0,
};

export const DEFAULT_SECTION_ANIMATION = {
  ...DEFAULT_COMPONENT_ANIMATION,
  staggerEnabled: true,
  staggerInterval: 100,
};

export const ANIMATION_TRIGGERS = [
  { id: 'load', label: 'Au chargement', description: 'Dès que la page s’affiche' },
  { id: 'scroll', label: 'Au scroll (entrée)', description: 'Quand la section entre dans l’écran' },
  { id: 'scrollOut', label: 'Au scroll (sortie)', description: 'Quand la section quitte l’écran' },
  {
    id: 'scrollContinuous',
    label: 'Parallaxe',
    description: 'Mouvement continu lié au défilement',
  },
];

export const ANIMATION_EASINGS = [
  { id: 'ease-out', label: 'Fluide (sortie)' },
  { id: 'ease-in', label: 'Accélération' },
  { id: 'ease-in-out', label: 'Entrée / sortie' },
  { id: 'ease', label: 'Standard' },
  { id: 'linear', label: 'Linéaire' },
  { id: 'spring', label: 'Rebond léger', css: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
  { id: 'smooth', label: 'Douce', css: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  { id: 'snappy', label: 'Vive', css: 'cubic-bezier(0.16, 1, 0.3, 1)' },
  { id: 'bounce', label: 'Élastique', css: 'cubic-bezier(0.68, -0.55, 0.27, 1.55)' },
];

export const ANIMATION_PRESETS = [
  { id: 'fadeIn', label: 'Fondu', icon: 'Circle' },
  { id: 'fadeInUp', label: 'Fondu ↑', icon: 'ArrowUp' },
  { id: 'fadeInDown', label: 'Fondu ↓', icon: 'ArrowDown' },
  { id: 'fadeInLeft', label: 'Fondu ←', icon: 'ArrowLeft' },
  { id: 'fadeInRight', label: 'Fondu →', icon: 'ArrowRight' },
  { id: 'slideInUp', label: 'Glisser ↑', icon: 'ChevronsUp' },
  { id: 'slideInDown', label: 'Glisser ↓', icon: 'ChevronsDown' },
  { id: 'slideInLeft', label: 'Glisser ←', icon: 'ChevronsLeft' },
  { id: 'slideInRight', label: 'Glisser →', icon: 'ChevronsRight' },
  { id: 'zoomIn', label: 'Zoom avant', icon: 'ZoomIn' },
  { id: 'zoomOut', label: 'Zoom arrière', icon: 'ZoomOut' },
  { id: 'bounceIn', label: 'Rebond', icon: 'Activity' },
  { id: 'popIn', label: 'Pop', icon: 'Sparkles' },
  { id: 'flipInX', label: 'Flip X', icon: 'FlipHorizontal' },
  { id: 'flipInY', label: 'Flip Y', icon: 'FlipVertical' },
  { id: 'rotateIn', label: 'Rotation', icon: 'RotateCw' },
  { id: 'blurIn', label: 'Flou', icon: 'Eye' },
  { id: 'revealUp', label: 'Révéler ↑', icon: 'PanelTop' },
  { id: 'revealLeft', label: 'Révéler ←', icon: 'PanelLeft' },
  { id: 'swingIn', label: 'Balancement', icon: 'MoveHorizontal' },
  { id: 'rubberBand', label: 'Élastique', icon: 'Waves' },
  { id: 'pulseIn', label: 'Pulsation', icon: 'Heart' },
];

const PRESET_IDS = new Set(ANIMATION_PRESETS.map((p) => p.id));
const TRIGGER_IDS = new Set(ANIMATION_TRIGGERS.map((t) => t.id));
const EASING_IDS = new Set(ANIMATION_EASINGS.map((e) => e.id));
const TIMING_IDS = new Set(ANIMATION_TIMING_MODES.map((t) => t.id));

function clampNumber(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function normalizeComponentAnimation(raw) {
  const base = { ...DEFAULT_COMPONENT_ANIMATION, ...(raw || {}) };
  return {
    ...base,
    enabled: base.enabled === true,
    preset: PRESET_IDS.has(base.preset) ? base.preset : DEFAULT_COMPONENT_ANIMATION.preset,
    trigger: TRIGGER_IDS.has(base.trigger) ? base.trigger : DEFAULT_COMPONENT_ANIMATION.trigger,
    easing: EASING_IDS.has(base.easing) ? base.easing : DEFAULT_COMPONENT_ANIMATION.easing,
    timing: TIMING_IDS.has(base.timing) ? base.timing : DEFAULT_COMPONENT_ANIMATION.timing,
    afterComponentId: String(base.afterComponentId || ''),
    delayAfterMs: clampNumber(base.delayAfterMs, 0, 5000, 0),
    sectionOrder: clampNumber(base.sectionOrder, 0, 99, 0),
    duration: clampNumber(base.duration, 100, 5000, DEFAULT_COMPONENT_ANIMATION.duration),
    delay: clampNumber(base.delay, 0, 5000, DEFAULT_COMPONENT_ANIMATION.delay),
    distance: clampNumber(base.distance, 0, 400, DEFAULT_COMPONENT_ANIMATION.distance),
    scale: clampNumber(base.scale, 0.1, 2, DEFAULT_COMPONENT_ANIMATION.scale),
    rotate: clampNumber(base.rotate, 0, 360, DEFAULT_COMPONENT_ANIMATION.rotate),
    blur: clampNumber(base.blur, 0, 40, DEFAULT_COMPONENT_ANIMATION.blur),
    scrollThreshold: clampNumber(base.scrollThreshold, 0, 1, DEFAULT_COMPONENT_ANIMATION.scrollThreshold),
    scrollOnce: base.scrollOnce !== false,
    scrollOffset: clampNumber(base.scrollOffset, -400, 400, DEFAULT_COMPONENT_ANIMATION.scrollOffset),
    parallaxSpeed: clampNumber(base.parallaxSpeed, 0.05, 2, DEFAULT_COMPONENT_ANIMATION.parallaxSpeed),
    parallaxDirection: base.parallaxDirection === 'down' ? 'down' : 'up',
  };
}

export function normalizeSectionAnimation(raw) {
  const base = normalizeComponentAnimation({ ...DEFAULT_SECTION_ANIMATION, ...(raw || {}) });
  return {
    ...base,
    staggerEnabled: raw?.staggerEnabled !== false,
    staggerInterval: clampNumber(raw?.staggerInterval, 0, 3000, DEFAULT_SECTION_ANIMATION.staggerInterval),
  };
}

export function getComponentAnimation(comp, viewport = 'desktop') {
  return normalizeComponentAnimation(getViewportAnimationSource(comp, viewport));
}

export function getSectionAnimation(section, viewport = 'desktop') {
  const raw =
    viewport !== 'desktop' && section?.[viewport]?.animation != null
      ? section[viewport].animation
      : section?.animation;
  return normalizeSectionAnimation(raw);
}

export function isComponentAnimationEnabled(comp, viewport = 'desktop') {
  return getComponentAnimation(comp, viewport).enabled;
}

export function isSectionAnimationEnabled(section, viewport = 'desktop') {
  return getSectionAnimation(section, viewport).enabled;
}

export function resolveEasingCss(easingId) {
  const found = ANIMATION_EASINGS.find((e) => e.id === easingId);
  return found?.css || found?.id || 'ease-out';
}

export function getAnimationCssVars(animation) {
  const config = normalizeComponentAnimation(animation);
  return {
    '--wc-anim-duration': `${config.duration}ms`,
    '--wc-anim-delay': `${config.delay}ms`,
    '--wc-anim-easing': resolveEasingCss(config.easing),
    '--wc-anim-distance': `${config.distance}px`,
    '--wc-anim-scale': String(config.scale),
    '--wc-anim-rotate': `${config.rotate}deg`,
    '--wc-anim-blur': `${config.blur}px`,
  };
}

export function getAnimationWrapperClassName(config, { active, pending = true } = {}) {
  const anim = normalizeComponentAnimation(config);
  if (!anim.enabled) return '';
  const classes = ['wc-anim-root', `wc-preset-${anim.preset}`];
  if (anim.trigger === 'scrollOut') classes.push('wc-anim-reverse');
  if (anim.trigger === 'scrollContinuous') {
    classes.push('wc-anim-parallax');
    if (active) classes.push('wc-anim-parallax-active');
  } else if (active) {
    classes.push('wc-anim-active');
  } else if (pending && anim.trigger !== 'scrollOut') {
    classes.push('wc-anim-pending');
  }
  return classes.join(' ');
}

export function patchComponentAnimation(prev, patch) {
  return normalizeComponentAnimation({ ...(prev || {}), ...(patch || {}) });
}

export function patchSectionAnimation(prev, patch) {
  return normalizeSectionAnimation({ ...(prev || {}), ...(patch || {}) });
}

export function getSectionComponentSequence(section, viewport = 'desktop') {
  return sortComponentsByGrid(section?.components || [], viewport).filter(
    (c) => !isComponentHidden(c, viewport),
  );
}

export function getComponentSequenceIndex(comp, section, viewport = 'desktop') {
  const sequence = getSectionComponentSequence(section, viewport);
  const manualOrder = getComponentAnimation(comp, viewport).sectionOrder;
  if (manualOrder > 0) {
    return manualOrder - 1;
  }
  const idx = sequence.findIndex((c) => c.id === comp.id);
  return idx >= 0 ? idx : 0;
}

export function computeSectionStaggerDelay(comp, section, sectionAnim, viewport = 'desktop') {
  const compAnim = getComponentAnimation(comp, viewport);
  if (!sectionAnim.staggerEnabled) return compAnim.delay;
  const order = getComponentSequenceIndex(comp, section, viewport);
  return compAnim.delay + order * sectionAnim.staggerInterval;
}

export function resolveEffectiveAnimation(comp, section, viewport = 'desktop') {
  const compAnim = getComponentAnimation(comp, viewport);
  if (!compAnim.enabled) {
    return { ...compAnim, enabled: false, usesSectionTrigger: false, waitForComponentId: '' };
  }

  const sectionAnim = getSectionAnimation(section, viewport);
  const sectionEnabled = sectionAnim.enabled;

  if (compAnim.timing === 'after' && compAnim.afterComponentId) {
    const baseTrigger = sectionEnabled ? sectionAnim.trigger : compAnim.trigger;
    return {
      ...compAnim,
      trigger: baseTrigger,
      scrollThreshold: sectionEnabled ? sectionAnim.scrollThreshold : compAnim.scrollThreshold,
      scrollOnce: sectionEnabled ? sectionAnim.scrollOnce : compAnim.scrollOnce,
      scrollOffset: sectionEnabled ? sectionAnim.scrollOffset : compAnim.scrollOffset,
      delay: compAnim.delayAfterMs,
      usesSectionTrigger: sectionEnabled,
      waitForComponentId: compAnim.afterComponentId,
    };
  }

  if (compAnim.timing === 'section' && sectionEnabled) {
    return {
      ...compAnim,
      trigger: sectionAnim.trigger,
      scrollThreshold: sectionAnim.scrollThreshold,
      scrollOnce: sectionAnim.scrollOnce,
      scrollOffset: sectionAnim.scrollOffset,
      delay: sectionAnim.delay + computeSectionStaggerDelay(comp, section, sectionAnim, viewport),
      usesSectionTrigger: true,
      waitForComponentId: '',
    };
  }

  return {
    ...compAnim,
    usesSectionTrigger: false,
    waitForComponentId: '',
  };
}

export function describeComponentAnimationTiming(comp, section, viewport = 'desktop') {
  const anim = getComponentAnimation(comp, viewport);
  if (!anim.enabled) return 'Désactivée';
  const sectionAnim = getSectionAnimation(section, viewport);

  if (anim.timing === 'after' && anim.afterComponentId) {
    const ref = (section?.components || []).find((c) => c.id === anim.afterComponentId);
    const refLabel = ref ? getComponentDisplayName(ref) : 'Composant';
    return `Après « ${refLabel} » + ${anim.delayAfterMs} ms`;
  }

  if (anim.timing === 'section' && sectionAnim.enabled) {
    const order = getComponentSequenceIndex(comp, section, viewport) + 1;
    const delay = sectionAnim.delay + computeSectionStaggerDelay(comp, section, sectionAnim, viewport);
    return `Séquence section (#${order}) · ${delay} ms`;
  }

  if (anim.delay > 0) return `Indépendante · ${anim.delay} ms`;
  return 'Indépendante';
}
